import { Appointment } from "../models/admissions.model";

/** Encounter-like object that may carry claim status from the API. */
export type EncounterClaimStatusSource =
  | Appointment
  | {
      claimStatus?: string | null;
      claim_status?: string | null;
      encounterStatus?: Record<string, unknown> | string | null;
      encounter_status?: Record<string, unknown> | null;
      encounterStatusRow?: Record<string, unknown> | null;
    }
  | Record<string, unknown>
  | null
  | undefined;

function readEncounterStatusRow(
  record: Record<string, unknown>
): Record<string, unknown> | undefined {
  for (const key of ["encounterStatusRow", "encounter_status"] as const) {
    const value = record[key];
    if (value && typeof value === "object" && !Array.isArray(value)) {
      return value as Record<string, unknown>;
    }
  }
  const encounterStatus = record["encounterStatus"];
  if (
    encounterStatus &&
    typeof encounterStatus === "object" &&
    !Array.isArray(encounterStatus)
  ) {
    return encounterStatus as Record<string, unknown>;
  }
  return undefined;
}

function readNestedStatusField(
  row: Record<string, unknown> | null | undefined,
  camel: string,
  snake: string
): string {
  if (!row || typeof row !== "object") {
    return "";
  }
  const raw = row[camel] ?? row[snake];
  if (raw == null) {
    return "";
  }
  return String(raw).trim();
}

/**
 * Flatten `encounterStatus` / `encounter_status` and snake_case billing fields onto the appointment
 * so headers match list-clinic rows after `GET …/encounters/details/:id`.
 */
export function normalizeEncounterAppointmentBillingFields<T extends Appointment>(
  appointment: T
): T {
  if (!appointment || typeof appointment !== "object") {
    return appointment;
  }
  const next = { ...appointment } as T & Record<string, unknown>;
  const record = next as Record<string, unknown>;

  const statusRow = readEncounterStatusRow(record);

  if (statusRow) {
    const clinical = readNestedStatusField(
      statusRow,
      "clinicalStatus",
      "clinical_status"
    );
    const billing = readNestedStatusField(
      statusRow,
      "billingStatus",
      "billing_status"
    );
    const signReq = readNestedStatusField(
      statusRow,
      "signReqStatus",
      "sign_req_status"
    );
    const claim = readNestedStatusField(statusRow, "claimStatus", "claim_status");
    const authStatus = readNestedStatusField(
      statusRow,
      "authStatus",
      "auth_status"
    );
    const authRequirement = readNestedStatusField(
      statusRow,
      "authRequirementStatus",
      "auth_requirement_status"
    );
    if (clinical && !next.clinicalStatus) {
      next.clinicalStatus = clinical;
    }
    if (billing && !next.billingStatus) {
      next.billingStatus = billing;
    }
    if (signReq && !next.signReqStatus) {
      next.signReqStatus = signReq;
    }
    if (claim && !next.claimStatus) {
      next.claimStatus = claim;
    }
    if (authStatus && !next.authStatus) {
      next.authStatus = authStatus;
    }
    if (authRequirement && !next.authRequirementStatus) {
      next.authRequirementStatus = authRequirement;
    }
  }

  if (!next.claimStatus) {
    const topClaim = readNestedStatusField(record, "claimStatus", "claim_status");
    if (topClaim) {
      next.claimStatus = topClaim;
    }
  }
  if (!next.clinicalStatus) {
    const topClinical = readNestedStatusField(
      record,
      "clinicalStatus",
      "clinical_status"
    );
    if (topClinical) {
      next.clinicalStatus = topClinical;
    }
  }
  if (!next.authStatus) {
    const topAuth = readNestedStatusField(record, "authStatus", "auth_status");
    if (topAuth) {
      next.authStatus = topAuth;
    }
  }
  if (!next.authRequirementStatus) {
    const topAuthRequirement = readNestedStatusField(
      record,
      "authRequirementStatus",
      "auth_requirement_status"
    );
    if (topAuthRequirement) {
      next.authRequirementStatus = topAuthRequirement;
    }
  }

  return next;
}

/**
 * Read claim status when the source exposes it (top-level or nested encounter status).
 * Returns `undefined` when the field is absent (unknown), `""` when explicitly empty/null.
 */
export function resolveEncounterClaimStatus(
  source: EncounterClaimStatusSource
): string | undefined {
  if (!source || typeof source !== "object") {
    return undefined;
  }
  const record = source as Record<string, unknown>;
  const statusRow = readEncounterStatusRow(record);
  const nestedClaim = readNestedStatusField(statusRow, "claimStatus", "claim_status");
  if (nestedClaim) {
    return nestedClaim;
  }
  if (!("claimStatus" in record) && !("claim_status" in record)) {
    return undefined;
  }
  const raw = record["claimStatus"] ?? record["claim_status"];
  if (raw == null) {
    return "";
  }
  return String(raw).trim();
}

/** True when claim status is explicitly empty or null (not when unknown). */
export function isEncounterClaimStatusEmpty(
  claimStatus: string | undefined
): boolean {
  return claimStatus !== undefined && claimStatus === "";
}
